import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CrmSubNav } from '@/components/crm/CrmSubNav';
import { CrmEmpresaSelector } from '@/components/crm/CrmEmpresaSelector';
import { useCrmEmpresaAtiva } from '@/hooks/useCrmEmpresaAtiva';
import { crmContatosService, crmContasService } from '@/services/crmApi';
import { CrmImportCsvDialog } from '@/components/crm/CrmImportCsvDialog';
import { CrmContato, CrmConta } from '@/types/crm';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, User, Edit, Trash2, Loader2, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

const NONE_VALUE = '__none__';

const EMPTY_FORM = {
  nome: '',
  conta_id: '',
  email: '',
  telefone: '',
  cargo: '',
};

export default function CrmContatos() {
  const { isSuperAdmin, empresas, selectedEmpresaId, setSelectedEmpresaId, empresaId } = useCrmEmpresaAtiva();
  const [searchParams, setSearchParams] = useSearchParams();
  const contaIdFilter = searchParams.get('conta_id') || '';

  const [contatos, setContatos] = useState<CrmContato[]>([]);
  const [contas, setContas] = useState<CrmConta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroTexto, setFiltroTexto] = useState('');

  const contatosFiltrados = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();
    if (!texto) return contatos;
    return contatos.filter((c) => [c.nome, c.email].some((campo) => campo?.toLowerCase().includes(texto)));
  }, [contatos, filtroTexto]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContato, setEditingContato] = useState<CrmContato | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formInitial, setFormInitial] = useState(EMPTY_FORM);
  const [showUnsaved, setShowUnsaved] = useState(false);

  const hasChanges = useCallback(
    () => JSON.stringify(form) !== JSON.stringify(formInitial),
    [form, formInitial],
  );

  const loadContas = async () => {
    if (isSuperAdmin && !empresaId) {
      setContas([]);
      return;
    }
    try {
      const result = await crmContasService.getAll(isSuperAdmin ? empresaId : undefined);
      if (result.success) setContas(result.data);
    } catch (error) {
      console.error('Error loading contas:', error);
    }
  };

  const loadData = async () => {
    if (isSuperAdmin && !empresaId) {
      setContatos([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await crmContatosService.getAll(contaIdFilter || undefined, isSuperAdmin ? empresaId : undefined);
      if (result.success) {
        setContatos(result.data);
      }
    } catch (error) {
      console.error('Error loading contatos:', error);
      toast.error('Erro ao carregar contatos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContas();
  }, [empresaId]);

  useEffect(() => {
    loadData();
  }, [contaIdFilter, empresaId]);

  const contaFiltroNome = contas.find((c) => c.id === contaIdFilter)?.razao_social;

  const handleSave = async () => {
    if (isSuperAdmin && !empresaId) {
      toast.error('Selecione uma empresa antes de criar um contato');
      return;
    }
    try {
      const payload = {
        nome: form.nome,
        conta_id: form.conta_id || null,
        email: form.email || null,
        telefone: form.telefone || null,
        cargo: form.cargo || null,
        empresa_id: isSuperAdmin ? empresaId : undefined,
      };

      const result = editingContato
        ? await crmContatosService.update(editingContato.id, payload)
        : await crmContatosService.create(payload);

      if (result.success) {
        toast.success(editingContato ? 'Contato atualizado' : 'Contato criado');
        loadData();
        closeDialog();
      } else {
        toast.error(result.error || 'Erro ao salvar contato');
      }
    } catch (error) {
      toast.error('Erro ao salvar contato');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      const result = await crmContatosService.delete(deleteConfirmId);
      if (result.success) {
        toast.success('Contato excluído');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao excluir contato');
      }
    } catch (error) {
      toast.error('Erro ao excluir contato');
    }
    setDeleteConfirmId(null);
  };

  const openEdit = (contato: CrmContato) => {
    const values = {
      nome: contato.nome,
      conta_id: contato.conta_id || '',
      email: contato.email || '',
      telefone: contato.telefone || '',
      cargo: contato.cargo || '',
    };
    setEditingContato(contato);
    setForm(values);
    setFormInitial(values);
    setIsDialogOpen(true);
  };

  const openNew = () => {
    const values = { ...EMPTY_FORM, conta_id: contaIdFilter || '' };
    setEditingContato(null);
    setForm(values);
    setFormInitial(values);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingContato(null);
    setForm(EMPTY_FORM);
    setFormInitial(EMPTY_FORM);
  };

  const handleCloseDialog = useCallback(() => {
    if (hasChanges()) {
      setShowUnsaved(true);
    } else {
      closeDialog();
    }
  }, [hasChanges]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <CrmSubNav
          active="contatos"
          right={
            isSuperAdmin ? (
              <CrmEmpresaSelector empresas={empresas} value={selectedEmpresaId} onChange={setSelectedEmpresaId} />
            ) : undefined
          }
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Contatos</h1>
            <p className="text-muted-foreground">
              {contaFiltroNome ? (
                <span className="inline-flex items-center gap-2">
                  Filtrado por conta: <span className="font-medium text-foreground">{contaFiltroNome}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => setSearchParams({})}
                    title="Limpar filtro"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </span>
              ) : (
                'Pessoas cadastradas no CRM'
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <CrmImportCsvDialog
              titulo="Importar Contatos via CSV"
              templateNomeArquivo="modelo-contatos.csv"
              templateColunas={['nome', 'email', 'telefone', 'cargo', 'conta', 'responsavel']}
              templateExemplo={['Juliana Ferreira', 'juliana@acme.com', '(11) 98888-0000', 'Compras', 'Acme Distribuidora Ltda', '']}
              onImport={(file) => crmContatosService.importCsv(file, { empresaId: isSuperAdmin ? empresaId : undefined })}
              onSuccess={loadData}
              disabled={isSuperAdmin && !empresaId}
              disabledMotivo="Selecione uma empresa primeiro"
            />
            <Button onClick={openNew} disabled={isSuperAdmin && !empresaId}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Contato
            </Button>
          </div>
        </div>

        {isSuperAdmin && !empresaId ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Selecione uma empresa acima para ver e gerenciar os contatos do CRM
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou e-mail..."
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                  className="h-9 pl-9"
                />
              </div>
              <span className="text-sm text-muted-foreground">
                {contatosFiltrados.length} de {contatos.length} contato(s)
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contato</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contatosFiltrados.map((contato) => (
                  <TableRow key={contato.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{contato.nome}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {contato.conta?.razao_social || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{contato.cargo || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[contato.email, contato.telefone].filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(contato)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(contato.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {contatosFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      {contatos.length === 0 ? 'Nenhum contato cadastrado' : 'Nenhum contato encontrado para essa busca'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContato ? 'Editar Contato' : 'Novo Contato'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Maria Silva"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="conta">Conta</Label>
                <Select
                  value={form.conta_id || NONE_VALUE}
                  onValueChange={(v) => setForm((f) => ({ ...f, conta_id: v === NONE_VALUE ? '' : v }))}
                >
                  <SelectTrigger id="conta">
                    <SelectValue placeholder="Sem conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Sem conta</SelectItem>
                    {contas.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.razao_social}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo</Label>
                <Input
                  id="cargo"
                  value={form.cargo}
                  onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                  placeholder="Ex: Comprador"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={form.telefone}
                  onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!form.nome}>
              {editingContato ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsaved}
        onOpenChange={setShowUnsaved}
        onConfirm={() => {
          setShowUnsaved(false);
          closeDialog();
        }}
        onCancel={() => setShowUnsaved(false)}
      />

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
